import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { useField, useFormikContext } from "formik";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { parseAddressParts } from "../../../helpers/helpers";
import Configuration from "../../../config";
import SummaryItem from "../components/SummaryItem/SummaryItem";
import { useSelector } from "react-redux";
import "./EgonAddressField.scss";

export default function EgonAddressField(props) {
  let {
    errorText,
    onSelect,
    onError,
    onChange,
    placeholder,
    suggestionType,
    invalidAddressMessage,
    egonLength,
    egonOrder,
    city,
    step,
    borderError,
    validationSchema,
    ...rest
  } = props;

  const stateCurrentStep = useSelector(
    (state) => state.caseGeneratorCurrentStep
  );
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();

  const [isInvalidAddress, setIsInvalidAddress] = useState(false);
  const [doneTypingInterval] = useState(500);
  const typingTimer = useRef(null);
  const type = useRef("");
  const selected = useRef(false);
  const address = useRef("");
  const listItems = useRef([]);
  const autocompleteInput = useRef(null);
  const city_restriction_id = useRef(null);
  const currentCity = useRef("");
  const username = useRef("WPT04CGRC01");
  const password = useRef("9QUANDP8");
  const url_suggest = useRef("https://wpt14.egoncloud.com/api/suggest");
  const url_validate = useRef(
    "https://wpwsc.egoncloud.com:1252/Egon/EgonNormJSON"
  );
  const isRequired = validationSchema?.fields[field.name]?._exclusive?.required;

  const { summaryMode } = rest;
  const { t } = useTranslation();
  const messageInvalidAddress = invalidAddressMessage
    ? invalidAddressMessage
    : t("cases.errors.address_invalid");

  const setEgonEnvVariables = () => {
    const env = Configuration.get("REACT_APP_ENVIRONMENT");
    username.current = env === "production" ? "WPT04GRC01" : "WPT04CGRC01";
    password.current = env === "production" ? "GTVUQ9WS" : "9QUANDP8";
    url_suggest.current =
      env === "production"
        ? "https://wpt11.egoncloud.com/api/suggest"
        : "https://wpt14.egoncloud.com/api/suggest";
    url_validate.current =
      env === "production"
        ? "https://egonws.egoncloud.com:1253/Egon/EgonNormJSON"
        : "https://wpwsc.egoncloud.com:1252/Egon/EgonNormJSON";
  };

  const defineAddressType = useCallback(() => {
    switch (suggestionType) {
      case "regions":
        type.current = "region";
        break;
      case "cities":
        type.current = "city";
        break;
      case "address":
      default:
        type.current = "street";
        break;
    }
  }, [suggestionType]);

  const handleSuggestionSelected = useCallback(
    async (payload, data) => {
      if (onSelect) {
        let response = await fetch(
          `${url_validate.current}?USER=${username.current}&PASW=${
            password.current
          }&CDXISO=${data.iso3}&CDPOBJ=${data.id}${
            data.number ? "&DSXESP=".concat(data.number) : ""
          }`,
          { method: "GET" }
        );
        const responsejsonValidate = response && (await response.json());
        const egonCodeResponse = {
          tipo_Indirizzo: "",
          egonCodeRegion:
            `${responsejsonValidate.SOG[0].INR_620.CDPPLCOBJ.CDPREG.lValue}` ||
            "",
          egonCodeState:
            `${responsejsonValidate.SOG[0].INR_620.CDPPLCOBJ.CDPDPT.lValue}` ||
            "",
          egonCodeCity:
            `${responsejsonValidate.SOG[0].INR_620.CDPPLCOBJ.CDPCNL.lValue}` ||
            "",
          egonCodeStreet:
            `${responsejsonValidate.SOG[0].INR_620.CDPSTRST1.CDPSTR.lValue}` ||
            "",
          egonCodeHouseNum:
            `${responsejsonValidate.SOG[0].INR_620.CIVICOST1.CDPCIV.lValue}` ||
            "",
          addressMatchCode:
            `${responsejsonValidate.SOG[0].INR_620.ALTRO.CDXMATCOD}` || "",
        };
        setIsInvalidAddress(false);
        selected.current = true;
        onSelect(
          parseAddressParts(payload.address_components),
          setFieldValue,
          egonCodeResponse
        );
      }
    },
    [onSelect, setFieldValue]
  );

  const transformData = useCallback((data) => {
    let payload = null;
    switch (type.current) {
      case "street":
        payload = {
          address_components: [
            {
              long_name: data.number,
              short_name: data.number,
              types: ["street_number"],
            },
            {
              long_name: data.zipcode,
              short_name: data.zipcode,
              types: ["postal_code"],
            },
            {
              long_name: data.street,
              short_name: data.street,
              types: ["route"],
            },
            {
              long_name: data.city,
              short_name: data.city,
              types: ["locality", "political"],
            },
            {
              long_name: data.city,
              short_name: data.city,
              types: ["administrative_area_level_3", "political"],
            },
            {
              long_name: data.province,
              short_name: data.province_code,
              types: ["administrative_area_level_2", "political"],
            },
            {
              long_name: data.region,
              short_name: data.region,
              types: ["administrative_area_level_1", "political"],
            },
            {
              long_name: data.country,
              short_name: data.iso3,
              types: ["country", "political"],
            },
          ],
        };
        break;
      case "city":
      default:
        payload = {
          address_components: [
            {
              long_name: data.city,
              short_name: data.city,
              types: ["locality", "political"],
            },
            {
              long_name: data.city,
              short_name: data.city,
              types: ["administrative_area_level_3", "political"],
            },
            {
              long_name: data.province,
              short_name: data.province_code,
              types: ["administrative_area_level_2", "political"],
            },
            {
              long_name: data.region,
              short_name: data.region,
              types: ["administrative_area_level_1", "political"],
            },
            {
              long_name: data.country,
              short_name: data.iso3,
              types: ["country", "political"],
            },
          ],
        };
        break;
    }
    return payload;
  }, []);

  const onClickListItem = useCallback(
    (ev, responsejson) => {
      let selectedAddress = ev.target.innerText;
      if (selectedAddress.includes("snc")) {
        selectedAddress = selectedAddress.replaceAll(", snc", "");
        selectedAddress = selectedAddress.replaceAll(" snc", "");
      }

      const data = responsejson.find((addr) => {
        return (
          (type.current === "street" &&
            addr.street ===
              selectedAddress.split(",")[0].replaceAll(` ${addr.number}`, "") &&
            addr.city ===
              (addr.zipcode
                ? selectedAddress.split(",")[2].split(" (")[0].replace(" ", "")
                : selectedAddress
                    .split(",")[1]
                    .split(" (")[0]
                    .replace(" ", ""))) ||
          (type.current === "city" &&
            addr.city === selectedAddress.split(" (")[0])
        );
      });
      if (data) {
        const payload = transformData(data);
        handleSuggestionSelected(payload, data);
      }
      address.current = selectedAddress;
    },
    [transformData, handleSuggestionSelected]
  );

  const onSelectListItem = useCallback(
    (responsejson) => {
      for (const item of listItems.current) {
        if (item)
          item.addEventListener("click", (ev) =>
            onClickListItem(ev, responsejson)
          );
      }
    },
    [onClickListItem]
  );

  const doneTyping = useCallback(
    async (event, saveSelect) => {
      let value = event.target.value;
      if (borderError) {
        onError(value, setFieldValue);
      }
      if (!saveSelect) {
        selected.current = false;
        address.current = value;
      }

      if (value.includes("snc")) {
        value = value.replaceAll(", snc", "");
        value = value.replaceAll(" snc", "");
      }

      const query =
        type.current === "street" ? value.split(" ").join("+") : value;

      let response;
      if (city_restriction_id.current) {
        response = await fetch(
          `${url_suggest.current}/${type.current}?app_key=${username.current}@${password.current}&query=${query}&restrict_level=city&restrict_id=${city_restriction_id.current}`,
          { method: "GET" }
        );
      } else {
        response = await fetch(
          `${url_suggest.current}/${type.current}?app_key=${username.current}@${password.current}&query=${query}`,
          { method: "GET" }
        );
      }
      const responsejson = response && (await response.json());
      const autocompleteList = document
        .getElementById(egonOrder ? `egon-widget-${egonOrder}` : "egon-widget")
        .getElementsByTagName("ul");
      listItems.current =
        autocompleteList &&
        autocompleteList.length &&
        autocompleteList[autocompleteList.length - 1].getElementsByTagName(
          "li"
        );
      if (response.json && responsejson.error) {
        onError(value, setFieldValue);
      }
      if (listItems.current) {
        onSelectListItem(responsejson);
      }
    },
    [address, onError, onSelectListItem, setFieldValue, type, egonOrder]
  );

  const onAutocompleteKeyup = useCallback(
    (event) => {
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(
        () => doneTyping(event),
        doneTypingInterval
      );
    },
    [typingTimer, doneTyping, doneTypingInterval]
  );

  const onAutocompleteKeydown = useCallback(() => {
    clearTimeout(typingTimer.current);
  }, [typingTimer]);

  const onAutocompleteFocusout = useCallback(() => {
    if (!selected.current) {
      onChange(setFieldValue, address.current);
    }
  }, [address, onChange, setFieldValue]);

  const addListeners = useCallback(() => {
    if (autocompleteInput.current) {
      autocompleteInput.current.addEventListener("keyup", onAutocompleteKeyup);
      autocompleteInput.current.addEventListener(
        "keydown",
        onAutocompleteKeydown
      );
      autocompleteInput.current.addEventListener("focus", (ev) =>
        doneTyping(ev, true)
      );
      autocompleteInput.current.addEventListener(
        "focusout",
        onAutocompleteFocusout
      );
    }
  }, [
    onAutocompleteKeyup,
    onAutocompleteKeydown,
    doneTyping,
    onAutocompleteFocusout,
  ]);

  const customerInitialize = useCallback(async () => {
    if (
      document.getElementById(
        egonOrder ? `egon-widget-${egonOrder}` : "egon-widget"
      )
    ) {
      const egon = document
        .getElementById(egonOrder ? `egon-widget-${egonOrder}` : "egon-widget")
        .getElementsByClassName("autocomplete");
      if (city && city !== "") {
        if (currentCity.current === city) return;
        currentCity.current = city;
        if (
          step == undefined ||
          stateCurrentStep == undefined ||
          step >= stateCurrentStep
        ) {
          const response = await fetch(
            `${url_suggest.current}/city?app_key=${username.current}@${password.current}&query=${city}`,
            { method: "GET" }
          );
          const data = response && (await response.json());
          if (data && data.length) city_restriction_id.current = data[0].id;
        }
      } else if (egon.length) {
        if (autocompleteInput.current) return;
        else {
          const inputs = document
            .getElementById(
              egonOrder ? `egon-widget-${egonOrder}` : "egon-widget"
            )
            .getElementsByClassName("autocomplete-input");
          autocompleteInput.current = inputs[0];
          addListeners();
          if (field.value && field.value !== "") {
            autocompleteInput.current.value = field.value;
            selected.current = true;
          }
          return;
        }
      }
      window.egonVar.egonAutocomplete(
        egonOrder ? `egon-widget-${egonOrder}` : "egon-widget",
        {
          app_key: username.current + "@" + password.current,
          user: username.current,
          pasw: password.current,
          restrict_level: city_restriction_id.current ? "city" : null,
          restrict_id: city_restriction_id.current || null,
          egonOrder: egonOrder ? egonOrder : 1,
          url_suggest: `${url_suggest.current}/${type.current}`,
          url_validate: url_validate.current,
          placeholder:
            typeof placeholder === "object"
              ? t(placeholder.props.i18nKey)
              : placeholder,
        }
      );
      if (egon.length > 1) {
        egon[0].remove();
        const inputs = document
          .getElementById(
            egonOrder ? `egon-widget-${egonOrder}` : "egon-widget"
          )
          .getElementsByClassName("autocomplete-input");
        autocompleteInput.current = inputs[0];
        addListeners();
      } else {
        const inputs = document
          .getElementById(
            egonOrder ? `egon-widget-${egonOrder}` : "egon-widget"
          )
          .getElementsByClassName("autocomplete-input");
        autocompleteInput.current = inputs[0];
        addListeners();
        if (field.value && field.value !== "") {
          autocompleteInput.current.value = field.value;
          selected.current = true;
        }
      }
      updateLayout();
    }
  }, [placeholder, t, type, city, addListeners, field.value, egonOrder]);

  useEffect(() => {
    setEgonEnvVariables();
    defineAddressType();
    customerInitialize();
    addListeners();

    return () => {
      if (autocompleteInput.current) {
        autocompleteInput.current.removeEventListener(
          "keyup",
          onAutocompleteKeyup
        );
        autocompleteInput.current.removeEventListener(
          "keydown",
          onAutocompleteKeydown
        );
        autocompleteInput.current.removeEventListener("focus", (ev) =>
          doneTyping(ev, true)
        );
        autocompleteInput.current.removeEventListener(
          "focusout",
          onAutocompleteFocusout
        );
      }
      for (const item of listItems.current) {
        item.removeEventListener("click", (ev) => onClickListItem(ev, null));
      }
    };
  }, [
    defineAddressType,
    egonLength,
    doneTyping,
    customerInitialize,
    onAutocompleteKeyup,
    onAutocompleteKeydown,
    onAutocompleteFocusout,
    onClickListItem,
    addListeners,
  ]);

  useEffect(() => {
    updateLayout();
  }, [meta.touched, meta.error]);

  const updateLayout = () => {
    if (autocompleteInput.current) {
      autocompleteInput.current.setAttribute("id", field.name);
      autocompleteInput.current.setAttribute("required", isRequired);
      autocompleteInput.current.classList.add("form-control");
      if (meta.touched && meta.error) {
        autocompleteInput.current.classList.add("is-invalid");
        autocompleteInput.current.setAttribute(
          "aria-describedby",
          field.name + "-error"
        );
      } else {
        autocompleteInput.current.classList.remove("is-invalid");
        autocompleteInput.current.removeAttribute("aria-describedby");
      }
    }
  };
  return (
    <Form.Group>
      {!summaryMode ? (
        <>
          {rest.label && (
            <Form.Label className="paragraph-small-fs9" htmlFor={field.name}>
              {rest.label}
            </Form.Label>
          )}
          <Fragment>
            <div
              id={egonOrder ? `egon-widget-${egonOrder}` : "egon-widget"}
              className={"egon-autocomplete"}
            ></div>
            {meta.touched && meta.error && (
              <p
                className="form-error-text paragraph-small-form"
                role="alert"
                id={field.name + "-error"}
              >
                {meta.error}
              </p>
            )}
            {isInvalidAddress && (
              <p
                className="form-error-text paragraph-small-form"
                role="alert"
                id={field.name + "-error"}
              >
                {messageInvalidAddress}
              </p>
            )}
          </Fragment>
        </>
      ) : (
        <>
          {rest.label && (
          <span className="paragraph-small-fs9">
            {rest.label}
          </span>
          )}
          <SummaryItem value={field.value} error={meta.error} />
        </>
      )}
      {props.subtitle ? (
        <Form.Text
          style={{
            border: "1px solid #b1894d",
            backgroundColor: "#f9f1c6",
            padding: "8px",
            borderRadius: "4px",
          }}
          className="text-muted"
        >
          <div style={{ width: "50%", color: "#b1894d", fontWeight: "bold" }}>
            {props.subtitle}
          </div>
        </Form.Text>
      ) : null}
    </Form.Group>
  );
}
